import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { issuerLabel, rpc, useAction, useIssuers, useOrg, type PersonLite } from "@vixio/data";
import { PersonPicker } from "@vixio/core-ui";
import {
  Button, Callout, DataTable, DateInput, DescriptionList, Dialog, DocInput, MoneyInput, NumberInput, Select, TextInput,
  formatDate, formatMoney, monthStartIso, todayIso, addDaysIso,
} from "@vixio/ui";
import { METHODS, newIdempotencyKey, useAccounts, useCategories, type InstallmentView, type Method } from "./api";

// ---------------------------------------------------------------- registrar recebimento

export function SettleDialog({ inst, open, onOpenChange }: { inst: InstallmentView | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const org = useOrg();
  const accounts = useAccounts(org.id);
  const [amount, setAmount] = useState<number | null>(null);
  const [paidOn, setPaidOn] = useState(todayIso());
  const [method, setMethod] = useState<Method>("pix");
  const [account, setAccount] = useState("");
  const [custom, setCustom] = useState(false);
  const [fine, setFine] = useState<number | null>(null);
  const [interest, setInterest] = useState<number | null>(null);
  const [discount, setDiscount] = useState<number | null>(0);
  const [reason, setReason] = useState("");
  const [key, setKey] = useState("");

  useEffect(() => {
    if (open && inst) {
      setAmount(inst.open_cents);
      setPaidOn(todayIso());
      setMethod("pix");
      setCustom(false);
      setDiscount(0);
      setReason("");
      setKey(newIdempotencyKey("ui-receipt"));
    }
  }, [open, inst]);

  const fee = useQuery({
    queryKey: ["finance", "late_fee", inst?.installment_id, amount, paidOn],
    enabled: Boolean(inst && amount && paidOn),
    queryFn: async () =>
      (
        await rpc("finance", "late_fee", {
          p_amount_cents: amount ?? 0,
          p_due: inst!.due_date,
          p_paid_on: paidOn,
          p_fine_pct: Number(inst!.fine_pct),
          p_interest_month_pct: Number(inst!.interest_month_pct),
          p_grace_days: inst!.grace_days,
        })
      )[0] ?? { days_late: 0, fine_cents: 0, interest_cents: 0 },
  });
  const policy = fee.data ?? { days_late: 0, fine_cents: 0, interest_cents: 0 };
  useEffect(() => {
    if (!custom) {
      setFine(policy.fine_cents);
      setInterest(policy.interest_cents);
    }
  }, [policy.fine_cents, policy.interest_cents, custom]);

  const overriding = custom && (fine !== policy.fine_cents || interest !== policy.interest_cents || (discount ?? 0) > 0);
  const total = (amount ?? 0) + (fine ?? 0) + (interest ?? 0) - (discount ?? 0);

  const settle = useAction(
    () =>
      rpc("finance", "settle_installment", {
        p_installment: inst!.installment_id,
        p_amount_cents: amount,
        p_paid_on: paidOn,
        p_method: method,
        p_account: account || null,
        p_idempotency_key: key,
        p_fine_cents: custom ? fine : null,
        p_interest_cents: custom ? interest : null,
        p_discount_cents: custom ? discount ?? 0 : 0,
        p_override_reason: overriding ? reason.trim() : null,
      }),
    {
      invalidate: [["finance"]],
      success: (r) => `Recebimento de ${formatMoney(r.total_cents)} registrado${r.idempotent ? " (já existia)" : ""}`,
      onSuccess: () => onOpenChange(false),
    },
  );
  if (!inst) return null;
  const invalid = !amount || amount <= 0 || amount > inst.open_cents || (overriding && reason.trim().length < 5) || paidOn > todayIso();

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Registrar recebimento"
      description={`${inst.counterparty_name} · parcela ${inst.number}${inst.label ? ` (${inst.label})` : ""} · vence ${formatDate(inst.due_date)}`}
      onSubmit={() => !invalid && settle.mutate(undefined)}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={settle.isPending} disabled={invalid}>
            Registrar {formatMoney(total)}
          </Button>
        </>
      }
    >
      <div className="vx-stack">
        <DescriptionList items={[["Em aberto", <span className="num">{formatMoney(inst.open_cents)}</span>], ["Descrição", inst.description]]} />
        <div className="vx-form-grid">
          <MoneyInput label="Principal recebido" value={amount} onChange={setAmount} max={inst.open_cents} hint="Padrão: o saldo da parcela." autoFocus />
          <DateInput label="Data do pagamento" value={paidOn} onChange={setPaidOn} max={todayIso()} />
          <Select label="Meio" value={method} onChange={(v) => setMethod(v)} options={Object.entries(METHODS).map(([value, label]) => ({ value: value as Method, label }))} />
          <Select
            label="Conta"
            value={account}
            onChange={setAccount}
            placeholder="Sem conta"
            options={(accounts.data ?? []).filter((a) => a.active).map((a) => ({ value: a.id, label: a.name }))}
          />
        </div>
        <fieldset className="vx-fieldset">
          <legend>Encargos pela política ({policy.days_late > 0 ? `${policy.days_late} dia(s) de atraso` : "em dia"})</legend>
          {!custom ? (
            <div className="vx-row" style={{ justifyContent: "space-between" }}>
              <span className="text-2">
                Multa <span className="num">{formatMoney(policy.fine_cents)}</span> · juros <span className="num">{formatMoney(policy.interest_cents)}</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => setCustom(true)}>
                Ajustar ou dar desconto
              </Button>
            </div>
          ) : (
            <div className="vx-form-grid">
              <MoneyInput label="Multa" value={fine} onChange={setFine} hint={`Política: ${formatMoney(policy.fine_cents)}`} />
              <MoneyInput label="Juros" value={interest} onChange={setInterest} hint={`Política: ${formatMoney(policy.interest_cents)}`} />
              <MoneyInput label="Desconto" value={discount} onChange={setDiscount} />
              <TextInput label="Motivo do ajuste" value={reason} onChange={setReason} hint="Obrigatório (5+ caracteres). Fica gravado com o cálculo da política." error={overriding && reason.trim().length < 5 ? "Informe o motivo." : null} />
            </div>
          )}
        </fieldset>
        <p className="vx-row" style={{ justifyContent: "space-between", fontSize: "var(--fs-14)" }}>
          <span>Total que entrou</span>
          <strong className="num">{formatMoney(total)}</strong>
        </p>
        {paidOn > todayIso() ? <Callout tone="crit">A data do pagamento não pode ser futura.</Callout> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- novo título a receber

export function NewReceivableDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated?: (id: string) => void }) {
  const org = useOrg();
  const issuers = useIssuers(org.id);
  const categories = useCategories(org.id);
  const [person, setPerson] = useState<PersonLite | null>(null);
  const [name, setName] = useState("");
  const [doc, setDoc] = useState("");
  const [docOk, setDocOk] = useState(true);
  const [description, setDescription] = useState("");
  const [total, setTotal] = useState<number | null>(null);
  const [count, setCount] = useState<number | null>(1);
  const [firstDue, setFirstDue] = useState(addDaysIso(todayIso(), 7));
  const [issuer, setIssuer] = useState("");
  const [category, setCategory] = useState("");
  const [competence, setCompetence] = useState(monthStartIso(todayIso()));
  const [key, setKey] = useState("");
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    if (open) {
      setKey(newIdempotencyKey("ui-receivable"));
      setSubmitted(false);
    }
  }, [open]);
  useEffect(() => {
    if (person && !name) setName(person.company_name || person.full_name);
  }, [person, name]);

  const plan = useQuery({
    queryKey: ["finance", "split", total, count, firstDue],
    enabled: Boolean(total && total > 0 && count && count > 0 && firstDue),
    queryFn: async () => (await rpc("finance", "split_installments", { p_total_cents: total!, p_count: count!, p_first_due: firstDue, p_interval_months: 1 })) ?? [],
  });

  const create = useAction(
    () =>
      rpc("finance", "create_receivable", {
        p_org: org.id,
        p_counterparty_name: name.trim(),
        p_total_cents: total!,
        p_description: description.trim(),
        p_issuer: issuer || null,
        p_person: person?.id ?? null,
        p_counterparty_doc: doc || null,
        p_installments: count ?? 1,
        p_first_due: firstDue,
        p_category: category || null,
        p_competence_month: competence || null,
        p_idempotency_key: key,
      }),
    {
      invalidate: [["finance"]],
      success: "Conta a receber criada",
      onSuccess: (id) => {
        onOpenChange(false);
        onCreated?.(id);
      },
    },
  );
  const revenueCats = useMemo(() => (categories.data ?? []).filter((c) => c.kind === "revenue" && c.active), [categories.data]);
  const errors = {
    name: submitted && name.trim().length < 2 ? "Informe o pagador." : null,
    desc: submitted && !description.trim() ? "Descreva o que está sendo cobrado." : null,
    total: submitted && (!total || total <= 0) ? "Informe o valor." : null,
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Nova conta a receber"
      description="Toda receita é fiscal. Valor e vencimentos não mudam depois de criados; correção é cancelamento ou renegociação."
      onSubmit={() => {
        setSubmitted(true);
        if (name.trim().length < 2 || !description.trim() || !total || total <= 0 || !docOk) return;
        create.mutate(undefined);
      }}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={create.isPending}>
            Criar
          </Button>
        </>
      }
    >
      <div className="vx-form-grid">
        <PersonPicker label="Pessoa (opcional)" value={person} onChange={setPerson} />
        <TextInput label="Pagador" value={name} onChange={setName} required error={errors.name} hint="Empresa ou pessoa que paga." />
        <DocInput
          label="CPF ou CNPJ do pagador"
          value={doc}
          onChange={(v, ok) => {
            setDoc(v);
            setDocOk(v === "" || ok);
          }}
        />
        <Select label="CNPJ emissor" value={issuer} onChange={setIssuer} placeholder="Padrão da organização" options={(issuers.data ?? []).filter((i) => i.active).map((i) => ({ value: i.id, label: issuerLabel(i) }))} />
        <TextInput className="span-2" label="Descrição" value={description} onChange={setDescription} required error={errors.desc} />
        <MoneyInput label="Valor total" value={total} onChange={setTotal} required error={errors.total} />
        <NumberInput label="Parcelas" value={count} onChange={setCount} min={1} max={60} />
        <DateInput label="Primeiro vencimento" value={firstDue} onChange={setFirstDue} />
        <DateInput label="Competência" type="month" value={competence} onChange={setCompetence} />
        <Select className="span-2" label="Categoria (DRE)" value={category} onChange={setCategory} placeholder="Automática pela oferta ou sem categoria" options={revenueCats.map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))} />
      </div>
      {plan.data && plan.data.length > 1 ? (
        <div style={{ marginTop: 16 }}>
          <DataTable
            bare
            caption="Plano de parcelas"
            data={plan.data}
            rowKey={(r) => String(r.number)}
            columns={[
              { key: "number", header: "Parcela", kind: "number" },
              { key: "due_date", header: "Vencimento", kind: "date" },
              { key: "amount_cents", header: "Valor", kind: "money" },
            ]}
          />
          <p className="muted" style={{ fontSize: "var(--fs-12)", marginTop: 6 }}>
            Partes iguais; o resto dos centavos vai para a última.
          </p>
        </div>
      ) : null}
    </Dialog>
  );
}
